import { forwardRef, memo, type ReactNode } from 'react';
import trim from 'lodash/trim';
import isNil from 'lodash/isNil';
import toSafeInteger from 'lodash/toSafeInteger';
import { Link as RouterLink } from 'react-router-dom';
import { useIntl, FormattedMessage, FormattedNumber } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
// Skillmore UI Components
import { shape } from '@empathco/ui-components/src/styles/themeOptions';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
// local imports
import { RouterLinkState } from '../helpers/routerParams';
// SCSS imports
import { item, card, newCountMsg, button } from './DashboardCard.module.scss';

type DashboardCardProps = {
  readonly children: ReactNode;
  readonly title: string;
  readonly disclaimer?: string;
  readonly newMessage: string;
  readonly link: string;
  readonly linkState?: RouterLinkState;
  readonly count?: number;
  readonly newCount?: number;
  readonly altCount?: number;
  readonly hasChart?: boolean;
  readonly wide?: boolean;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
}

const DashboardCard = forwardRef<HTMLDivElement, DashboardCardProps>(({
  children,
  title,
  disclaimer,
  newMessage,
  link,
  linkState,
  count,
  newCount,
  altCount,
  hasChart = false,
  pending = false,
  failed = false,
  wide = false
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();
  const hasNewCount = !isNil(newCount) && newCount >= 1;
  const hasAltCount = !isNil(altCount) && altCount >= 1;
  const columnLayout = hasNewCount && hasAltCount;
  const disclaimerMsg = disclaimer ? trim(formatMessage({ id: disclaimer, defaultMessage: disclaimer })) : undefined;
  const newCnt = hasNewCount || hasAltCount ? (
    <Typography variant="subtitle2" className={newCountMsg}>
      <FormattedMessage
          id={newMessage}
          values={{
            count: toSafeInteger(newCount),
            altCount: toSafeInteger(altCount)
          }}
      />
    </Typography>
  ) : undefined;
  return (
    <Grid
        ref={ref}
        size={{
          xs: 12,
          sm: 10,
          md: 6,
          lg: wide ? undefined : 4
        }}
        container
        alignItems="stretch"
        className={item}
    >
      <Card elevation={9} className={card}>
        {(failed && <FetchFailedAlert/>) ||
        (pending && <LoadingPlaceholder fullHeight/>) || (
          <>
            <Box
                p={columnLayout ? undefined : 3}
                px={columnLayout ? 3 : undefined}
                pt={columnLayout ? 3 : undefined}
                color="text.label"
                bgcolor="background.paper"
                display="flex"
                flexWrap="wrap"
            >
              <BoxTypography flex="4 1 0" pr={1} variant="subtitle1">
                <FormattedMessage id={title} defaultMessage={title} values={{ br: <br/> }}/>
              </BoxTypography>
              {!isNil(count) && !isNil(newCount) ? (
                <Box flex="1 1 0" display="flex" flexDirection="column" alignItems="flex-end">
                  <Typography variant="h2">
                    <FormattedNumber value={toSafeInteger(count)}/>
                  </Typography>
                  {columnLayout ? undefined : newCnt}
                </Box>
              ) : undefined}
              {disclaimerMsg ? (
                <BoxTypography
                    variant="body2"
                    color="text.secondary"
                    pt={0.75}
                    pb={columnLayout ? 0.75 : undefined}
                    width="100%"
                    flexBasis="100%"
                >
                  {disclaimerMsg}
                </BoxTypography>
              ) : undefined}
            </Box>
            {columnLayout && newCnt ? (
              <Box px={3} pb={3} flex="1 1 0" display="flex" justifyContent="flex-end">
                {newCnt}
              </Box>
            ) : undefined}
            <Box
                flex={hasChart ? '1 1 auto' : '1 0 0'}
                display={hasChart ? 'flex' : undefined}
                position={hasChart ? 'relative' : undefined}
                alignItems={hasChart ? 'center' : undefined}
                justifyContent={hasChart ? 'center' : undefined}
            >
              {children}
            </Box>
            <Box
                px={{ xs: 3.5, sm: 10 }}
                py={{ xs: 3, sm: 5 }}
                bgcolor="background.button"
                border={shape.thinBorderWidth}
                borderColor="delimiterColor"
            >
              <Button
                  color="primary"
                  variant="contained"
                  disableElevation
                  fullWidth
                  className={button}
                  component={RouterLink}
                  to={link}
                  state={linkState}
              >
                <FormattedMessage id="board.view_all"/>
              </Button>
            </Box>
          </>
        )}
      </Card>
    </Grid>
  );
});

DashboardCard.displayName = 'DashboardCard';

export default memo(DashboardCard);
